import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import SectionLayout from "@/components/SectionLayout";
import Login from "@/pages/Login";
import HubHome from "@/pages/HubHome";
import Dashboard from "@/pages/Dashboard";
import LogWork from "@/pages/LogWork";
import Logs from "@/pages/Logs";
import LogDetail from "@/pages/LogDetail";
import ReviewQueue from "@/pages/ReviewQueue";
import Organizations from "@/pages/Organizations";
import Contacts from "@/pages/Contacts";
import Engagements from "@/pages/Engagements";
import EngagementDetail from "@/pages/EngagementDetail";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Evaluate from "@/pages/Evaluate";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Directory from "@/pages/Directory";
import Consultants from "@/pages/Consultants";
import MySchools from "@/pages/MySchools";
import VisitWork from "@/pages/VisitWork";
import VisitDetail from "@/pages/VisitDetail";
import ReflectionWork from "@/pages/ReflectionWork";
import Contracts from "@/pages/Contracts";
import ClientInvoices from "@/pages/ClientInvoices";
import Imports from "@/pages/Imports";
import Evaluations from "@/pages/Evaluations";
import Invoices from "@/pages/Invoices";
import Documents from "@/pages/Documents";
import PaymentTiers from "@/pages/PaymentTiers";
import Profiles from "@/pages/Profiles";
import MyProfile from "@/pages/MyProfile";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Proposals from "@/pages/Proposals";
import ProposalDetail from "@/pages/ProposalDetail";

function Protected({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center" data-testid="auth-loading">
        <div className="text-sm font-bold text-navy/60 animate-pulse">Loading Operations Hub…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/evaluate/:token" element={<Evaluate />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/ops" element={<Dashboard />} />
        <Route path="/log-work" element={<LogWork />} />
        <Route path="/log-work/:logId" element={<LogWork />} />
        <Route path="/visit-work" element={<VisitWork />} />
        <Route path="/visit-work/:visitId" element={<VisitWork />} />
        <Route path="/visits/:visitId" element={<VisitDetail />} />
        <Route path="/reflections/:reflectionId" element={<ReflectionWork />} />
        <Route path="/contracts" element={<Protected adminOnly><Contracts /></Protected>} />
        <Route path="/client-invoices" element={<Protected adminOnly><ClientInvoices /></Protected>} />
        <Route path="/imports" element={<Protected adminOnly><Imports /></Protected>} />
        <Route path="/logs" element={<Logs />} />
        <Route path="/logs/:logId" element={<LogDetail />} />
        <Route path="/review" element={<Protected adminOnly><ReviewQueue /></Protected>} />
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/engagements" element={<Engagements />} />
        <Route path="/engagements/:engagementId" element={<EngagementDetail />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/evaluations" element={<Evaluations />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/directory" element={<Directory />} />
        <Route path="/consultants" element={<Protected adminOnly><Consultants /></Protected>} />
        <Route path="/my-schools" element={<MySchools />} />
        <Route path="/settings" element={<Protected adminOnly><Settings /></Protected>} />
      </Route>
      <Route element={<Protected><SectionLayout /></Protected>}>
        <Route path="/documents" element={<Documents />} />
        <Route path="/payment-tiers" element={<Protected adminOnly><PaymentTiers /></Protected>} />
        <Route path="/profiles" element={<Protected adminOnly><Profiles /></Protected>} />
        <Route path="/profiles/:consultantId" element={<Protected adminOnly><Profiles /></Protected>} />
        <Route path="/my-profile" element={<MyProfile />} />
        <Route path="/clients" element={<Protected adminOnly><Clients /></Protected>} />
        <Route path="/clients/:orgId" element={<Protected adminOnly><ClientDetail /></Protected>} />
        <Route path="/proposals" element={<Protected adminOnly><Proposals /></Protected>} />
        <Route path="/proposals/:proposalId" element={<Protected adminOnly><ProposalDetail /></Protected>} />
      </Route>
      <Route path="/" element={<Protected><HubHome /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
